import dayjs from 'dayjs';
import FloatingFilterSelect from '../../../../component/Common/Datagrid/Component/FloatingFilterRenderer/FloatingFilterSelect';
import { CELL_TYPE_SWITCH } from '../../../../constant/Common/DataGrid/cellType';
import { DAMAGE_STATUS } from '../../../../constant/Safety/DamageReport/standardCodes';
import { STYLE_COLUMN_ALIGNMENT } from '../../../../style/Safety/Common/style';
import datetimeComparator from '../../../Common/Datagrid/Comparator/datetimeComparator';
import generateCellRenderer from '../../../Common/Datagrid/Generator/generateCellRenderer';
import { createOptionalSelectOption } from '../../../Common/Option/Generator/selectOptionCreator';
import { createBooleanData, createStatusData } from './dataSetterGenerator';
import generateDatetimeFilters from '../../../Common/Datagrid/Generator/generateDatetimeFilters';

const createDamageColumnDefs = ({
  completedPatchMutator,
  filterDivisionList,
  isEditable,
  disabledRef,
}) => {
  const columnDef = [
    {
      headerName: 'STATUS',
      width: 75,
      field: 'status',
      editable: false,
      valueGetter: gridParam => {
        const value = createStatusData(gridParam?.data?.status);
        return value;
      },
      floatingFilterComponent: FloatingFilterSelect,
      floatingFilterComponentParams: {
        option: createOptionalSelectOption({
          items: Object?.keys?.(DAMAGE_STATUS),
          isGridFilter: true,
        }),
      },
    },
    {
      headerName: 'DIV',
      width: 38,
      field: 'div',
      editable: false,
      floatingFilterComponent: FloatingFilterSelect,
      floatingFilterComponentParams: {
        option: filterDivisionList,
      },
    },
    {
      headerName: 'YARD',
      width: 70,
      field: 'yard',
      editable: false,
    },
    {
      headerName: 'LOC',
      width: 72,
      field: 'yard_location',
      editable: false,
    },
    {
      headerName: 'CREATED BY',
      width: 94,
      field: 'created_by',
      editable: false,
    },
    {
      headerName: 'DMG DATE',
      width: 100,
      field: 'dmgDate',
      editable: false,
      cellClass: 'datetime',
      valueGetter: gridParam => {
        const stringReportDateTime = `${gridParam?.data?.report_date} ${gridParam?.data?.report_time}`;
        const value =
          stringReportDateTime && dayjs(stringReportDateTime)?.isValid()
            ? dayjs(stringReportDateTime)?.format('MM/DD/YY HH:mm')
            : '';
        return value;
      },
      comparator: datetimeComparator,
      ...generateDatetimeFilters(),
    },
    {
      headerName: 'DRIVER',
      width: 72,
      field: 'driver',
      editable: false,
    },
    {
      headerName: 'TRUCK #',
      width: 69,
      field: 'trk_no',
      editable: false,
    },
    {
      headerName: 'PLATE #',
      width: 90,
      field: 'trk_plate_no',
      editable: false,
    },
    {
      headerName: 'TRUCK DMG',
      headerClass: ['dmg-group-header', 'truck-dmg-group-header'],
      backgroundColor: '#FBE2D5',
      isColumnGroup: true,
      children: [
        {
          headerName: 'ABS',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'trk_abs',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_abs);
            return value;
          },
        },
        {
          headerName: 'BATTERY',
          cellClass: 'dmg-boolean-cell',
          width: 68,
          field: 'trk_battery',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_battery);
            return value;
          },
        },
        {
          headerName: 'ENGINE LIGHT',
          cellClass: 'dmg-boolean-cell',
          width: 61,
          field: 'trk_engine_light',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_engine_light);
            return value;
          },
        },
        {
          headerName: 'HOSES',
          cellClass: 'dmg-boolean-cell',
          width: 53,
          field: 'trk_hoses',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_hoses);
            return value;
          },
        },
        {
          headerName: 'WINDSHIELD',
          cellClass: 'dmg-boolean-cell',
          width: 93,
          field: 'trk_windshield',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_windshield);
            return value;
          },
        },
        {
          headerName: 'LIGHTS',
          cellClass: 'dmg-boolean-cell',
          width: 52,
          field: 'trk_lights',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_lights);
            return value;
          },
        },
        {
          headerName: 'MIRRORS',
          cellClass: 'dmg-boolean-cell',
          width: 70,
          field: 'trk_mirrors',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_mirrors);
            return value;
          },
        },
        {
          headerName: 'MUD FLAPS',
          cellClass: 'dmg-boolean-cell',
          width: 83,
          field: 'trk_mudflap',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_mudflap);
            return value;
          },
        },
        {
          headerName: 'REFLECTORS',
          cellClass: 'dmg-boolean-cell',
          width: 103,
          field: 'trk_reflectors',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_reflectors);
            return value;
          },
        },
        {
          headerName: 'TURN INDICATORS',
          cellClass: 'dmg-boolean-cell',
          width: 91,
          field: 'trk_turn_indicators',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(
              gridParam?.data?.trk_turn_indicators,
            );
            return value;
          },
        },
        {
          headerName: 'OTHER',
          width: 116,
          field: 'trk_others',
          hide: true,
        },
      ],
    },
    {
      headerName: 'STEER TIRES',
      headerClass: ['dmg-group-header', 'steer-tires-group-header'],
      backgroundColor: '#F7C7AC',
      isColumnGroup: true,
      children: [
        {
          headerName: 'LEFT',
          cellClass: 'dmg-boolean-cell',
          width: 54,
          field: 'st_left_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.st_left_dmg);
            return value;
          },
        },
        {
          headerName: 'RIGHT',
          cellClass: 'dmg-boolean-cell',
          width: 54,
          field: 'st_right_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.st_right_dmg);
            return value;
          },
        },
      ],
    },
    {
      headerName: 'DRIVE TIRES',
      headerClass: ['dmg-group-header', 'drive-tires-group-header'],
      backgroundColor: '#F1A983',
      isColumnGroup: true,
      children: [
        {
          headerName: 'LFO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_lfo_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_lfo_dmg);
            return value;
          },
        },
        {
          headerName: 'LFI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_lfi_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_lfi_dmg);
            return value;
          },
        },
        {
          headerName: 'RFO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_rfo_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_rfo_dmg);
            return value;
          },
        },
        {
          headerName: 'RFI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_rfi_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_rfi_dmg);
            return value;
          },
        },
        {
          headerName: 'LRO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_lro_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_lro_dmg);
            return value;
          },
        },
        {
          headerName: 'LRI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_lri_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_lri_dmg);
            return value;
          },
        },
        {
          headerName: 'RRO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_rro_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_rro_dmg);
            return value;
          },
        },
        {
          headerName: 'RRI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'dt_rri_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.dt_rri_dmg);
            return value;
          },
        },
      ],
    },
    {
      headerName: 'TRUCK DMG',
      backgroundColor: '#CCD3E3',
      isColumnGroup: true,
      children: [
        {
          headerName: 'BODY',
          width: 50,
          field: 'trk_dmg',
          editable: false,
          isSuppressExportExcel: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.trk_dmg);
            return value;
          },
          floatingFilterComponent: FloatingFilterSelect,
          floatingFilterComponentParams: {
            option: createOptionalSelectOption({
              items: ['Y', 'N'],
              isGridFilter: true,
            }),
          },
        },
        {
          headerName: 'TIRE',
          width: 50,
          field: 'trk_tire',
          editable: false,
          isSuppressExportExcel: true,
          cellRendererSelector: gridParam =>
            generateCellRenderer({
              gridParam,
              styles: {
                ...STYLE_COLUMN_ALIGNMENT.number,
              },
            }),
        },
      ],
    },
    {
      headerName: 'EQUIP #',
      width: 92,
      field: 'cont_no',
      editable: false,
    },
    {
      headerName: 'CHASSIS #',
      width: 86,
      field: 'ch_no',
      editable: false,
    },
    {
      headerName: 'CH PLATE #',
      width: 112,
      field: 'ch_plate_no',
      editable: false,
    },
    {
      headerName: 'EQUIPMENT / CHAS DMG',
      headerClass: ['dmg-group-header', 'equipment-group-header'],
      backgroundColor: '#DAE9F8',
      isColumnGroup: true,
      children: [
        {
          headerName: 'ABS',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'ch_abs',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_abs);
            return value;
          },
        },
        {
          headerName: 'AIR LEAK',
          cellClass: 'dmg-boolean-cell',
          width: 65,
          field: 'ch_air_leak',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_air_leak);
            return value;
          },
        },
        {
          headerName: 'BRAKE CHAMBER',
          cellClass: 'dmg-boolean-cell',
          width: 74,
          field: 'ch_brake_chamber',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_brake_chamber);
            return value;
          },
        },
        {
          headerName: 'BRAKE LIGHTS',
          cellClass: 'dmg-boolean-cell',
          width: 52,
          field: 'ch_brakes',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_brakes);
            return value;
          },
        },
        {
          headerName: 'HUB CAP',
          cellClass: 'dmg-boolean-cell',
          width: 65,
          field: 'ch_hubcap',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_hubcap);
            return value;
          },
        },
        {
          headerName: 'LANDING GEAR',
          cellClass: 'dmg-boolean-cell',
          width: 63,
          field: 'ch_landing_gear',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_landing_gear);
            return value;
          },
        },
        {
          headerName: 'LIGHTS',
          cellClass: 'dmg-boolean-cell',
          width: 52,
          field: 'ch_lights',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_lights);
            return value;
          },
        },
        {
          headerName: 'MUD FLAPS',
          cellClass: 'dmg-boolean-cell',
          width: 83,
          field: 'ch_mudflap',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_mudflap);
            return value;
          },
        },
        {
          headerName: '7-WAY PLUG',
          cellClass: 'dmg-boolean-cell',
          width: 45,
          field: 'ch_7_way_plug',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_7_way_plug);
            return value;
          },
        },
        {
          headerName: 'OTHER',
          field: 'ch_others',
          hide: true,
        },
      ],
    },
    {
      headerName: 'TRAILER OR CHASSIS TIRES',
      headerClass: ['dmg-group-header', 'equipment-tires-group-header'],
      backgroundColor: '#A6C9EC',
      isColumnGroup: true,
      children: [
        {
          headerName: 'LFO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_lfo_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_lfo_dmg);
            return value;
          },
        },
        {
          headerName: 'LFI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_lfi_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_lfi_dmg);
            return value;
          },
        },
        {
          headerName: 'RFO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_rfo_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_rfo_dmg);
            return value;
          },
        },
        {
          headerName: 'RFI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_rfi_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_rfi_dmg);
            return value;
          },
        },
        {
          headerName: 'LRO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_lro_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_lro_dmg);
            return value;
          },
        },
        {
          headerName: 'LRI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_lri_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_lri_dmg);
            return value;
          },
        },
        {
          headerName: 'RRO',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_rro_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_rro_dmg);
            return value;
          },
        },
        {
          headerName: 'RRI',
          cellClass: 'dmg-boolean-cell',
          width: 35,
          field: 'tct_rri_dmg',
          hide: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.tct_rri_dmg);
            return value;
          },
        },
      ],
    },
    {
      headerName: 'CHAS DMG',
      backgroundColor: '#909FC1',
      isColumnGroup: true,
      children: [
        {
          headerName: 'DMG',
          width: 50,
          field: 'ch_dmg',
          editable: false,
          isSuppressExportExcel: true,
          valueGetter: gridParam => {
            const value = createBooleanData(gridParam?.data?.ch_dmg);
            return value;
          },
          floatingFilterComponent: FloatingFilterSelect,
          floatingFilterComponentParams: {
            option: createOptionalSelectOption({
              items: ['Y', 'N'],
              isGridFilter: true,
            }),
          },
        },
        {
          headerName: 'TIRE',
          width: 50,
          field: 'ch_tire',
          editable: false,
          isSuppressExportExcel: true,
          cellRendererSelector: gridParam =>
            generateCellRenderer({
              gridParam,
              styles: {
                ...STYLE_COLUMN_ALIGNMENT.number,
              },
            }),
        },
      ],
    },
    {
      headerName: 'O / R',
      width: 50,
      field: 'ch_tire_or',
      editable: false,
      isSuppressExportExcel: true,
    },
    {
      headerName: 'REMARKS',
      field: 'remarks',
      editable: false,
    },
  ];

  if (isEditable) {
    columnDef?.push({
      headerName: 'COMPLETE',
      cellClass: 'dmg-boolean-cell',
      width: 100,
      field: 'completed',
      editable: false,
      clickable: true,
      valueGetter: params => {
        return params?.data?.completed ? 'Complete' : 'Incomplete';
      },
      cellRendererSelector: gridParam => {
        return generateCellRenderer({
          gridParam,
          cellType: CELL_TYPE_SWITCH,
          value: gridParam?.data?.completed,
          onChangeHandler: ({ ...props }) => {
            if (
              disabledRef?.current?.disabled &&
              disabledRef?.current?.disabledRow === gridParam?.rowIndex
            ) {
              return;
            }
            disabledRef.current.disabled = true;
            disabledRef.current.disabledRow = gridParam?.rowIndex;

            const { e, setChecked } = props;

            setChecked(!!e?.target?.checked);
            completedPatchMutator({ seq: gridParam?.data?.seq, setChecked });
          },
        });
      },
      floatingFilterComponent: FloatingFilterSelect,
      floatingFilterComponentParams: {
        option: [
          {
            label: 'All',
            value: '',
          },
          {
            label: 'Complete',
            value: 'Complete',
          },
          {
            label: 'Incomplete',
            value: 'Incomplete',
          },
        ],
      },
    });
  }
  return columnDef;
};

export default createDamageColumnDefs;
